import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";
import { Dropdown } from "primereact/dropdown";
import { useEffect, useRef, useState, useCallback } from "react";
import { FilterMatchMode } from "primereact/api";
import AppBreadcrumb from "../../components/CustomeBreadcrumb/AppBreadcrumb";
import {
  bookingStatusDropdownItems,
  convertDateFromTo,
  defaultPageRowOptions,
  defaultRowOptions,
  paginatorLinks,
  showingEntries,
  SkeletonbodyTemplate,
  Skeletonitems,
  getUTCStartOfDay,
  getUTCEndOfDay,
} from "../../appconfig/Settings";
import axiosInstance from "../../config/axiosInstance";
import { APP_BASE_URL } from "../../appconfig/Settings";
import { GET_OPERATOR_REPORT_DATA } from "../../config/ApiConstant";
import { getAllDetails } from "../../service/ProductService";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { operatorReportColumns } from "../../appconfig/DataTableColumns";
import { Badge } from "primereact/badge";
import { amountFormate, getBookingStatusSeverity } from "../../appconfig/AppHelper";
import { Image } from "primereact/image";
import { Dialog } from "primereact/dialog";

const OperatorReport = () => {
  document.title = "Operator Report | Agriown";

  const toast = useRef<any>(null);
  const [globalFilter, setGlobalFilter] = useState<any>("");

  // datatable filter value sets
  const emptyFilters = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  };
  const [filters, setFilters] = useState(emptyFilters);

  // Report list state
  const [reportList, setReportList] = useState<any>([]);

  // Loading/Page Loading
  const [pageLoad, setPageLoad] = useState<boolean>(false);

  // Pagination state (server-side)
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(defaultRowOptions);

  // Date Object
  const [dates, setDates] = useState<string | Date | Date[] | any | null>([
    null,
    null,
  ]);

  // Filter states
  const [selectedStatus, setSelectedStatus] = useState<any>(null);

  // Product dialog state
  const [productDialogVisible, setProductDialogVisible] =
    useState<boolean>(false);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);

  // useeffect for default date range (last 3 months)
  useEffect(() => {
    const today = new Date();
    const pastDate = new Date();
    pastDate.setMonth(today.getMonth() - 3);
    setDates([pastDate, today]);
  }, []);


  // get report list api - using axiosInstance directly to preserve pagination metadata
  const getReportList = useCallback(async (
    page: number = 1,
    limit: number = defaultRowOptions,
    startDate?: Date,
    endDate?: Date,
    searchKeyword?: string
  ) => {
    try {
      let queryUrl = `${GET_OPERATOR_REPORT_DATA}?page=${page}&limit=${limit}`;

      if (startDate && endDate) {
        const start_date = getUTCStartOfDay(startDate);
        const end_date = getUTCEndOfDay(endDate);
        queryUrl += `&start_date=${encodeURIComponent(start_date)}&end_date=${encodeURIComponent(end_date)}`;
      }


      if (selectedStatus) {
        queryUrl += `&booking_status=${encodeURIComponent(selectedStatus)}`;
      }
      if (searchKeyword) {
        queryUrl += `&search=${encodeURIComponent(searchKeyword)}`;
      }

      const response = await axiosInstance(APP_BASE_URL + queryUrl);
      const responseData = response?.data;

      // Handle both formats:
      // Direct: { total_records, current_page, total_pages, data: [...] }
      // Wrapped: { data: { total_records, current_page, total_pages, data: [...] } }
      let result;
      if (responseData?.total_records !== undefined) {
        result = responseData;
      } else if (responseData?.data?.total_records !== undefined) {
        result = responseData.data;
      } else {
        result = responseData;
      }

      if (result) {
        setReportList(Array.isArray(result) ? result : (result?.data || []));
        setTotalRecords(result?.total_records || 0);
        setPageLoad(true);
      } else {
        setReportList([]);
        setTotalRecords(0);
        setPageLoad(true);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message || "Failed to fetch report data",
      });
      setPageLoad(true);
    }
  }, [selectedStatus]);

  // useeffect get report list api
  useEffect(() => {
    const startDate = dates && dates[0] ? dates[0] : undefined;
    const endDate = dates && dates[1] ? dates[1] : undefined;
    
    // Debounce the API call for search
    const timer = setTimeout(() => {
      getReportList(currentPage, rowsPerPage, startDate, endDate, globalFilter);
    }, 500);

    return () => clearTimeout(timer);
  }, [dates, currentPage, rowsPerPage, selectedStatus, globalFilter, getReportList]);

  // handle page change
  const onPageChange = (event: any) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setCurrentPage(newPage);
    setRowsPerPage(event.rows);
  };

  // export csv
  const exportCSV = () => {
    if (!reportList?.length) {
      toast.current?.show({
        severity: "warn",
        summary: "Warning",
        detail: "No data available to export",
      });
      return;
    }

    const csvData = reportList.map((item: any) => {
      return {
        "Booking ID": item?.booking_id || "-",
        "Booking Date": item?.booking_date
          ? convertDateFromTo(item.booking_date)
          : "-",
        "Farmer Name": item?.farmer_name || "-",
        "Farmer Mobile": item?.farmer_mobile || "-",
        "Operator Name": item?.operator_name || "-",
        "Booking Status": item?.booking_status || "-",
        "Operator Charge": item?.operator_charge || 0,
        Village: item?.farmer_village || "-",
        Taluka: item?.farmer_taluka || "-",
        District: item?.farmer_district || "-",
        "Farm Name": item?.farm_name || "-",
        Crop: item?.crop || "-",
        "Crop Age": item?.crop_age || "-",
        "Area (Acre)": item?.area_of_acre || "-",
        "Spray For Problem": item?.spray_for_problem || "-",
        "Spray Type": item?.spray_type || "-",
        "Used Products": item?.used_products
          ?.map((p: any) => p.name)
          .join(", ") || "-",
        Transaction: item?.amount_transaction || "-",
        "Payment Method": item?.payment_method || "-",
        "Amount Paid": item?.amount_paid || 0,
        "Payment Status": item?.payment_status || "-",
      };
    });

    const csvRows = [];
    const headers = Object.keys(csvData[0]);
    csvRows.push(headers.join(","));

    csvData.forEach((row: any) => {
      const values = headers.map((header) =>
        `"${String(row[header]).replace(/"/g, '""')}"`
      );
      csvRows.push(values.join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `operator-report-${new Date().getTime()}.csv`
    );

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // global search
  const leftToolbarTemplate = () => {
    return (
      <>
        <span className="mr-2 md:mr-3 search-bar">
          <i className="pi pi-search"></i>
          <InputText
            type="search"
            className="search-input"
            value={globalFilter}
            onChange={(e) => {
              setGlobalFilter(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search..."
          />
        </span>
      </>
    );
  };

  //right toolbar
  const rightToolbarTemplate = () => {
    return (
      <>
        <Button
          icon="pi pi-file-excel"
          label="Export CSV"
          className="p-button-success mr-3"
          onClick={exportCSV}
        />

        <Dropdown
          value={selectedStatus}
          options={bookingStatusDropdownItems}
          onChange={(e) => {
            setSelectedStatus(e.value);
            setCurrentPage(1);
          }}
          optionLabel="name"
          optionValue="code"
          placeholder="Select Status"
          className="mr-2"
          showClear
        />
        <Calendar
          value={dates}
          className="mr-2 md:mr-3 order-cle"
          dateFormat="dd/mm/yy"
          onChange={(e) => setDates(e.value)}
          selectionMode="range"
          showIcon
          readOnlyInput
        />
        <Button
          icon="pi pi-filter-slash"
          label="Clear"
          onClick={() => clearAll()}
          className="md:mr-2 p-button-outlined p-button-primary"
        ></Button>
      </>
    );
  };

  //Clear All Filter
  const clearAll = () => {
    setFilters({ ...emptyFilters });
    setGlobalFilter("");
    setDates(null);

    setSelectedStatus(null);
    setCurrentPage(1);
  };

  // booking date template
  const bookingDateTemplate = (rowData: any) => {
    return (
      <span>
        {rowData?.booking_date
          ? convertDateFromTo(rowData.booking_date)
          : "-"}
      </span>
    );
  };

  // booking status template
  const bookingStatusTemplate = (rowData: any) => {
    const status = rowData?.booking_status;
    return <Badge value={status || "-"} severity={getBookingStatusSeverity(status)} />;
  };

  // payment status template
  const paymentStatusTemplate = (rowData: any) => {
    const status = rowData?.payment_status;
    let severity: any = undefined;
    switch (status?.toLowerCase()) {
      case "paid":
      case "completed":
        severity = "success";
        break;
      case "pending":
        severity = "warning";
        break;
      case "failed":
        severity = "danger";
        break;
      default:
        severity = "info";
    }
    return <Badge value={status || "-"} severity={severity} />;
  };

  // amount template
  const amountBodyTemplate = (rowData: any, field: string) => {
    const value = rowData?.[field];
    return <span>{value != null ? amountFormate(value) : "-"}</span>;
  };

  // used products template
  const usedProductsTemplate = (rowData: any) => {
    const products = rowData?.used_products;
    if (!products || products.length === 0) return <span>-</span>;

    return (
      <div>
        <Button
          label={`${products.length} Product(s)`}
          className="p-button-text p-button-sm"
          onClick={() => {
            setSelectedProducts(products);
            setProductDialogVisible(true);
          }}
        />
      </div>
    );
  };

  // image template for before/after spray
  const sprayImageTemplate = (rowData: any, field: string) => {
    const data = rowData?.[field];
    if (!data || !data.image) return <span>-</span>;

    return (
      <div className="flex flex-column align-items-center">
        <Image
          src={data.image}
          alt={field}
          width="60"
          height="60"
          preview
          style={{ borderRadius: "4px", objectFit: "cover" }}
        />
      </div>
    );
  };

  // simple image template for form_8a_image and aadhar_card_image
  const simpleImageTemplate = (rowData: any, field: string) => {
    const imageUrl = rowData?.[field];
    if (!imageUrl) return <span>-</span>;

    return (
      <Image
        src={imageUrl}
        alt={field}
        width="60"
        height="60"
        preview
        style={{ borderRadius: "4px", objectFit: "cover" }}
      />
    );
  };

  // global field template
  const fieldTemplate = (rowData: any, field: string) => {
    const value = rowData?.[field];
    return (
      <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
        {value != null && value !== "" ? value : "-"}
      </span>
    );
  };

  // get body template based on column field
  const getBodyTemplate = (field: string) => {
    switch (field) {
      case "booking_date":
        return bookingDateTemplate;
      case "booking_status":
        return bookingStatusTemplate;
      case "payment_status":
        return paymentStatusTemplate;
      case "operator_charge":
        return (rowData: any) => amountBodyTemplate(rowData, "operator_charge");
      case "amount_paid":
        return (rowData: any) => amountBodyTemplate(rowData, "amount_paid");
      case "used_products":
        return usedProductsTemplate;
      case "before_spray_image":
        return (rowData: any) =>
          sprayImageTemplate(rowData, "before_spray_image");
      case "after_spray_image":
        return (rowData: any) =>
          sprayImageTemplate(rowData, "after_spray_image");
      case "form_8a_image":
        return (rowData: any) =>
          simpleImageTemplate(rowData, "form_8a_image");
      case "aadhar_card_image":
        return (rowData: any) =>
          simpleImageTemplate(rowData, "aadhar_card_image");
      default:
        return (rowData: any) => fieldTemplate(rowData, field);
    }
  };

  return (
    <>
      {/* Breadcrumb */}
      <AppBreadcrumb
        currentMenu="Report"
        pageName="Operator Report"
        pageTitle="Operator Report"
      />

      <div className="grid crud-demo">
        <div className="col-12">
          <div className="card">
            <Toast ref={toast} />

            <Toolbar
              left={leftToolbarTemplate}
              right={rightToolbarTemplate}
            ></Toolbar>

            {pageLoad ? (
              <>
                <DataTable
                  value={reportList}
                  showGridlines
                  stripedRows
                  globalFilter={globalFilter || null}
                  filters={{ ...filters, global: { ...filters.global, value: globalFilter || null } }}
                  onFilter={(e: any) => setFilters(e.filters)}
                  globalFilterFields={[
                    "farmer_name",
                    "operator_name",
                    "booking_id",
                    "farmer_mobile",
                    "booking_status",
                    "payment_status",
                  ]}
                  rows={rowsPerPage}
                  paginator
                  lazy
                  first={(currentPage - 1) * rowsPerPage}
                  totalRecords={totalRecords}
                  onPage={onPageChange}
                  rowsPerPageOptions={defaultPageRowOptions}
                  className="datatable-responsive p-datatable-sm"
                  paginatorTemplate={paginatorLinks}
                  currentPageReportTemplate={showingEntries}
                  emptyMessage="No Data Found"
                  scrollable
                  scrollHeight="flex"
                  removableSort
                >
                  {operatorReportColumns.map((col, i) => (
                    <Column
                      key={col.field}
                      field={col.field}
                      header={col.header}
                      body={getBodyTemplate(col.field)}
                      sortable={[
                        "booking_id",
                        "booking_date",
                        "farmer_name",
                        "operator_name",
                        "amount_paid",
                        "operator_charge",
                      ].includes(col.field)}
                      style={{ minWidth: "150px" }}
                    />
                  ))}
                </DataTable>
              </>
            ) : (
              <>
                <DataTable
                  value={Skeletonitems}
                  className="datatable-responsive p-datatable-sm"
                >
                  {operatorReportColumns.slice(0, 8).map((col, i) => (
                    <Column
                      key={col.field}
                      field={col.field}
                      header={col.header}
                      body={SkeletonbodyTemplate}
                    />
                  ))}
                </DataTable>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Used Products Dialog */}
      <Dialog
        header="Used Products"
        visible={productDialogVisible}
        style={{ width: "500px" }}
        onHide={() => setProductDialogVisible(false)}
      >
        <div className="grid">
          {selectedProducts.map((product: any, index: number) => (
            <div key={index} className="col-12 mb-3">
              <div
                className="flex align-items-center gap-3 p-3 border-round"
                style={{ backgroundColor: "#f8f9fa", border: "1px solid #dee2e6" }}
              >
                {product.photo && (
                  <Image
                    src={product.photo}
                    alt={product.name}
                    width="60"
                    height="60"
                    preview
                    style={{ borderRadius: "8px", objectFit: "cover" }}
                  />
                )}
                <div className="flex flex-column">
                  <span className="font-semibold">{product.name || "-"}</span>
                  <span className="text-sm" style={{ color: "#6c757d" }}>
                    Type: {product.item_type || "-"}
                  </span>
                  <span className="text-sm" style={{ color: "#6c757d" }}>
                    Amount: {product.amount != null ? amountFormate(product.amount) : "-"}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Dialog>
    </>
  );
};

export default OperatorReport;
